import { BasecampRole } from "@utils/types";
import { useState } from "react";

import { Button, Input } from "@fronterahealth/frontera-ui-components";

import { type GetOrganizationsOutput, useBasecampOrganizationsUpdateCreate } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useBasecampRoles } from "@hooks/useBasecampRoles";
import { VERTICAL_ORG_ID_COPY, VERTICAL_ORG_ID_PATTERN } from "@pages/Organizations/constants";

interface VerticalOrgIdSectionProps {
  orgId: string;
  org: GetOrganizationsOutput;
  onSaved: () => Promise<unknown> | void;
}

/** Edits a Frontera EHR org's vertical organization id (`external_id`). Empty clears it. */
export const VerticalOrgIdSection: React.FC<VerticalOrgIdSectionProps> = ({ orgId, org, onSaved }) => {
  const savedValue = org.external_id ?? "";
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const value = pendingValue ?? savedValue;
  const trimmed = value.trim();
  const isDirty = pendingValue !== null && trimmed.toLowerCase() !== savedValue;
  const isValid = trimmed === "" || VERTICAL_ORG_ID_PATTERN.test(trimmed);

  const roles = useBasecampRoles();
  const canEdit = roles.includes(BasecampRole.ADMIN) || roles.includes(BasecampRole.ENABLEMENT_ADMIN);
  const { mutateAsync: updateOrganization } = useBasecampOrganizationsUpdateCreate();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let response: Awaited<ReturnType<typeof updateOrganization>>;
      try {
        response = await updateOrganization({ data: { organization_id: orgId, external_id: trimmed || null } });
      } catch {
        notifyError(VERTICAL_ORG_ID_COPY.saveFailed);
        return;
      }
      if (response.status !== 200) {
        notifyError(
          (response.data && "detail" in response.data ? response.data.detail : null) || VERTICAL_ORG_ID_COPY.saveFailed,
        );
        return;
      }
      notifySuccess(VERTICAL_ORG_ID_COPY.saved);
      try {
        await onSaved();
      } finally {
        setPendingValue(null);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-2 rounded-xl border border-border-medium bg-bg-surface p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="w-full sm:max-w-md">
          <Input
            name="vertical-org-id"
            label={VERTICAL_ORG_ID_COPY.label}
            inputSize="full"
            value={value}
            placeholder="00000000-0000-0000-0000-000000000000"
            disabled={!canEdit || isSaving}
            onChange={(e) => setPendingValue(e.target.value)}
          />
        </div>
        <Button
          variant="primary"
          buttonStyle="filled"
          size="sm"
          text={isSaving ? "Saving..." : "Save"}
          disabled={!canEdit || !isDirty || !isValid || isSaving}
          onClick={handleSave}
        />
      </div>
      <p className="text-xs text-text-secondary">
        {canEdit ? VERTICAL_ORG_ID_COPY.help : VERTICAL_ORG_ID_COPY.noPermission}
      </p>
      {!isValid && (
        <p role="alert" className="text-xs text-text-error-primary">
          {VERTICAL_ORG_ID_COPY.invalid}
        </p>
      )}
    </section>
  );
};
